import { useMutation } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import { billing } from '@/services/billing';
import type { SetupIntentPayload } from '@/services/billing/types';
import { getApiErrorMessage } from '@/utils/errors';
import type { CreditCardSubmitResult } from '../utils/credit-card-form-submit';

const UPDATE_CARD_IN_PROGRESS_ID = 'subscription.updateCard.inProgress';

export type UpdateCard = {
  /**
   * Stores a confirmed card. Resolves `true` once it is stored and `false` when it failed, so the
   * caller can keep its form open to retry — the failure itself is already surfaced by a snackbar.
   */
  submitCard: (result: CreditCardSubmitResult) => Promise<boolean>;
  isSubmitting: boolean;
};

/**
 * Stores the account's card, with the snackbar lifecycle and the vendor branching that go with it.
 *
 * The vendors settle differently. Stripe polls until the subscription reflects the new card and then
 * refreshes billing; Ordway re-fetches the card instead. A page that got that wrong would show the
 * old card until a reload.
 */
export function useUpdateCard(isOrdwayCustomer: boolean): UpdateCard {
  const snackbars = useSnackbars();

  const updateCardMutation = useMutation({
    mutationFn: async (result: CreditCardSubmitResult) => {
      // The confirmed Stripe SetupIntent is opaque here; the billing endpoint stores it as-is.
      await billing.updateCard.perform(
        // oxlint-disable-next-line typescript/consistent-type-assertions -- Stripe.js returns unknown and the API takes the object opaquely, so there is nothing to narrow against
        result.setupIntent as SetupIntentPayload | undefined,
      );
      snackbars.create({
        variant: 'default',
        id: UPDATE_CARD_IN_PROGRESS_ID,
        message: 'Updating your card…',
        // 0 disables auto-close; dismissed explicitly once the update is confirmed or fails.
        autoCloseTimeout: 0,
      });

      if (isOrdwayCustomer) {
        await billing.fetchOrdwayCardDetails.perform();
      } else {
        await billing.pollForSubscriptionTask.perform('complete');
        await billing.initialize.perform({ refresh: true });
      }
    },
    onSuccess: () => {
      snackbars.destroy(UPDATE_CARD_IN_PROGRESS_ID);
      snackbars.create({
        variant: 'default',
        id: 'subscription.updateCard.success',
        message: 'Your card details will be updated soon.',
      });
    },
    onError: (error) => {
      snackbars.destroy(UPDATE_CARD_IN_PROGRESS_ID);
      snackbars.create({
        variant: 'critical',
        id: 'subscription.updateCard.error',
        message: getApiErrorMessage(
          error,
          "Your card couldn't be updated. Contact win@customer.io for help setting up your subscription.",
        ),
      });
    },
  });

  return {
    submitCard: async (result: CreditCardSubmitResult) => {
      try {
        await updateCardMutation.mutateAsync(result);
        return true;
      } catch {
        // The mutation's onError already surfaced the failure; keep the form open to retry.
        return false;
      }
    },
    isSubmitting: updateCardMutation.isPending,
  };
}
